import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://panel.haymestasarim.com/api";

const BRAND = {
  blue: "#0B4F8A",
  blue2: "#083B68",
  orange: "#F97316",
  orange2: "#FB923C",
  bg: "#F6F8FC",
  card: "#FFFFFF",
  text: "#172033",
  muted: "#64748B",
  border: "rgba(15, 23, 42, 0.10)",
  softBlue: "rgba(11, 79, 138, 0.08)",
  softOrange: "rgba(249, 115, 22, 0.10)",
  green: "#16A34A",
  red: "#DC2626",
  amber: "#D97706",
};

function Grid({
  container = false,
  xs = 12,
  sm,
  md,
  lg,
  xl,
  spacing = 0,
  rowSpacing,
  columnSpacing,
  alignItems,
  justifyContent,
  children,
  sx = {},
  ...props
}) {
  const makeSpan = (value) => {
    const n = Math.max(1, Math.min(12, Number(value || 12)));
    return `span ${n} / span ${n}`;
  };

  if (container) {
    const gap = spacing || 0;
    const rowGap = rowSpacing ?? gap;
    const colGap = columnSpacing ?? gap;

    return (
      <Box
        {...props}
        sx={{
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          display: "grid",
          gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
          rowGap: (theme) =>
            typeof rowGap === "number" ? theme.spacing(rowGap) : rowGap,
          columnGap: (theme) =>
            typeof colGap === "number" ? theme.spacing(colGap) : colGap,
          alignItems,
          justifyContent,
          ...sx,
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      {...props}
      sx={{
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        gridColumn: {
          xs: makeSpan(xs),
          sm: makeSpan(sm ?? xs),
          md: makeSpan(md ?? sm ?? xs),
          lg: makeSpan(lg ?? md ?? sm ?? xs),
          xl: makeSpan(xl ?? lg ?? md ?? sm ?? xs),
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const LANGS = ["tr", "en", "de"];

const DICT = {
  tr: {
    title: "Yazar Kasa POS Entegrasyonu",
    shortTitle: "Yazar Kasa POS",
    subtitle:
      "Yazar kasa POS, fiziksel POS, sanal POS ve ödeme cihazı bağlantılarını tek panelden yönetin.",
    mobileHint:
      "Mobil görünüm aktif. Sekmeleri sağa-sola kaydırarak kullanabilirsiniz.",
    overview: "Genel Bakış",
    connections: "POS Bağlantıları",
    payments: "Ödeme İşlemleri",
    reports: "Raporlar",
    logsHelp: "Loglar & Yardım",
    activeConnections: "Aktif Bağlantı",
    todayPayments: "Bugünkü Ödeme",
    todayRevenue: "Bugünkü POS Ciro",
    failedPayments: "Hatalı İşlem",
    pendingPayments: "Bekleyen İşlem",
    refresh: "Yenile",
    addConnection: "Yeni POS Bağlantısı",
    edit: "Düzenle",
    save: "Kaydet",
    cancel: "Vazgeç",
    close: "Kapat",
    disable: "Pasife Al",
    test: "Bağlantıyı Test Et",
    active: "Aktif",
    inactive: "Pasif",
    passive: "Pasif",
    completed: "Başarılı",
    pending: "Bekliyor",
    failed: "Başarısız",
    cancelled: "İptal",
    country: "Ülke",
    currency: "Para Birimi",
    provider: "POS Sağlayıcı",
    status: "Durum",
    accountName: "Bağlantı Adı",
    connectionMode: "Bağlantı Modu",
    testMode: "Test Modu",
    liveMode: "Canlı Mod",
    apiSettings: "API / Cihaz Bilgileri",
    deviceSettings: "Cihaz Ayarları",
    cashSettings: "Kasa Ayarları",
    autoCash: "Başarılı ödemeyi kasaya otomatik işle",
    providerSelect: "POS sağlayıcı seç",
    allProviders: "Tüm sağlayıcılar",
    allCountries: "Tüm ülkeler",
    allStatuses: "Tüm durumlar",
    noData: "Henüz kayıt yok",
    startPayment: "Ödeme Başlat",
    testPayment: "Test Ödeme",
    account: "POS Bağlantısı",
    amount: "Tutar",
    paymentMethod: "Ödeme Tipi",
    card: "Kart / POS",
    cash: "Nakit",
    bank: "Banka / Havale",
    payment_link: "Ödeme Linki",
    qr: "QR Ödeme",
    customerName: "Müşteri Adı",
    description: "Açıklama",
    sourceModule: "Kaynak Modül",
    transactionDate: "İşlem Tarihi",
    dateFrom: "Başlangıç",
    dateTo: "Bitiş",
    search: "Ara",
    exportCsv: "CSV İndir",
    reportSummary: "Rapor Özeti",
    dailyBreakdown: "Günlük Dağılım",
    completedTotal: "Başarılı Toplam",
    completedCount: "Başarılı İşlem",
    failedCount: "Başarısız İşlem",
    pendingCount: "Bekleyen İşlem",
    cancelledCount: "İptal İşlem",
    terminalId: "Terminal ID",
    merchantId: "Merchant ID",
    approvalCode: "Onay Kodu",
    receiptNo: "Fiş No",
    externalTransactionId: "POS İşlem ID",
    cashProcessed: "Kasaya İşlendi",
    source: "Kaynak",
    logType: "Tip",
    action: "İşlem",
    message: "Mesaj",
    createdAt: "Oluşturma",
    updatedAt: "Güncelleme",
    saved: "Kaydedildi",
    updated: "Güncellendi",
    deleted: "Pasife alındı",
    connectionOk: "Bağlantı ayarları doğru görünüyor",
    paymentStarted: "Ödeme işlemi başlatıldı",
    errorGeneric: "İşlem sırasında hata oluştu",
    missingRequired: "Zorunlu alanları doldurun",
    helpTitle: "Bu bölüm nasıl çalışır?",
    help1Title: "1. Yazar kasa POS bağlantısı nasıl eklenir?",
    help1:
      "POS firması veya ödeme kuruluşundan alınan terminal, merchant, API key ve secret bilgileri POS Bağlantıları sekmesinden kaydedilir.",
    help2Title: "2. Test modu ne işe yarar?",
    help2:
      "Gerçek cihaz API bilgisi gelmeden ödeme akışını test eder. Başarılı simülasyonda ödeme kasaya otomatik aktarılabilir.",
    help3Title: "3. Kasa bağlantısı nasıl çalışır?",
    help3:
      "Ödeme başarılı olduğunda sistem cash_transactions tablosuna POS tahsilatı olarak gelir kaydı oluşturur ve aynı işlemi ikinci kez kasaya düşürmez.",
    help4Title: "4. Gerçek cihaz ne zaman bağlanır?",
    help4:
      "Pavo, Ingenico, Verifone, Hugin, Beko, INPOS veya sanal POS firmasından API/SDK dokümanı gelince ilgili sağlayıcı adaptörü bağlanır.",
  },

  en: {
    title: "Fiscal POS Integration",
    shortTitle: "Fiscal POS",
    subtitle:
      "Manage fiscal POS, physical POS, virtual POS and payment device connections from one panel.",
    mobileHint:
      "Mobile card view is active. Swipe tabs horizontally when needed.",
    overview: "Overview",
    connections: "POS Connections",
    payments: "Payments",
    reports: "Reports",
    logsHelp: "Logs & Help",
    activeConnections: "Active Connections",
    todayPayments: "Today's Payments",
    todayRevenue: "Today's POS Revenue",
    failedPayments: "Failed Payments",
    pendingPayments: "Pending Payments",
    refresh: "Refresh",
    addConnection: "New POS Connection",
    edit: "Edit",
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    disable: "Disable",
    test: "Test Connection",
    active: "Active",
    inactive: "Inactive",
    passive: "Passive",
    completed: "Successful",
    pending: "Pending",
    failed: "Failed",
    cancelled: "Cancelled",
    country: "Country",
    currency: "Currency",
    provider: "POS Provider",
    status: "Status",
    accountName: "Connection Name",
    connectionMode: "Connection Mode",
    testMode: "Test Mode",
    liveMode: "Live Mode",
    apiSettings: "API / Device Credentials",
    deviceSettings: "Device Settings",
    cashSettings: "Cash Settings",
    autoCash: "Post successful payments to cash automatically",
    providerSelect: "Select POS provider",
    allProviders: "All providers",
    allCountries: "All countries",
    allStatuses: "All statuses",
    noData: "No records yet",
    startPayment: "Start Payment",
    testPayment: "Test Payment",
    account: "POS Connection",
    amount: "Amount",
    paymentMethod: "Payment Method",
    card: "Card / POS",
    cash: "Cash",
    bank: "Bank / Transfer",
    payment_link: "Payment Link",
    qr: "QR Payment",
    customerName: "Customer Name",
    description: "Description",
    sourceModule: "Source Module",
    transactionDate: "Transaction Date",
    dateFrom: "Date From",
    dateTo: "Date To",
    search: "Search",
    exportCsv: "Export CSV",
    reportSummary: "Report Summary",
    dailyBreakdown: "Daily Breakdown",
    completedTotal: "Successful Total",
    completedCount: "Successful Payments",
    failedCount: "Failed Payments",
    pendingCount: "Pending Payments",
    cancelledCount: "Cancelled Payments",
    terminalId: "Terminal ID",
    merchantId: "Merchant ID",
    approvalCode: "Approval Code",
    receiptNo: "Receipt No",
    externalTransactionId: "POS Transaction ID",
    cashProcessed: "Posted to Cash",
    source: "Source",
    logType: "Type",
    action: "Action",
    message: "Message",
    createdAt: "Created",
    updatedAt: "Updated",
    saved: "Saved",
    updated: "Updated",
    deleted: "Disabled",
    connectionOk: "Connection settings look correct",
    paymentStarted: "Payment has been started",
    errorGeneric: "An error occurred",
    missingRequired: "Please fill required fields",
    helpTitle: "How does this module work?",
    help1Title: "1. How do I add a fiscal POS connection?",
    help1:
      "Terminal, merchant, API key and secret values from the POS provider are saved under POS Connections.",
    help2Title: "2. What is test mode?",
    help2:
      "It tests the payment flow before real device API credentials are available. Successful simulations can be posted to cash automatically.",
    help3Title: "3. How does cash posting work?",
    help3:
      "When payment succeeds, the system creates an income record in cash_transactions and prevents duplicate cash entries.",
    help4Title: "4. When is the real device connected?",
    help4:
      "Once API/SDK documentation is received from Pavo, Ingenico, Verifone, Hugin, Beko, INPOS or a virtual POS provider, the adapter is connected.",
  },

  de: {
    title: "Kassen-POS Integration",
    shortTitle: "Kassen-POS",
    subtitle:
      "Kassen-POS, physische POS-Geräte, virtuelle POS und Zahlungsgeräte zentral verwalten.",
    mobileHint:
      "Mobile Kartenansicht ist aktiv. Tabs können seitlich gescrollt werden.",
    overview: "Übersicht",
    connections: "POS-Verbindungen",
    payments: "Zahlungen",
    reports: "Berichte",
    logsHelp: "Logs & Hilfe",
    activeConnections: "Aktive Verbindungen",
    todayPayments: "Zahlungen heute",
    todayRevenue: "POS-Umsatz heute",
    failedPayments: "Fehlerhafte Zahlungen",
    pendingPayments: "Wartende Zahlungen",
    refresh: "Aktualisieren",
    addConnection: "Neue POS-Verbindung",
    edit: "Bearbeiten",
    save: "Speichern",
    cancel: "Abbrechen",
    close: "Schließen",
    disable: "Deaktivieren",
    test: "Verbindung testen",
    active: "Aktiv",
    inactive: "Inaktiv",
    passive: "Passiv",
    completed: "Erfolgreich",
    pending: "Wartend",
    failed: "Fehlgeschlagen",
    cancelled: "Storniert",
    country: "Land",
    currency: "Währung",
    provider: "POS-Anbieter",
    status: "Status",
    accountName: "Verbindungsname",
    connectionMode: "Verbindungsmodus",
    testMode: "Testmodus",
    liveMode: "Live-Modus",
    apiSettings: "API- / Gerätedaten",
    deviceSettings: "Geräteeinstellungen",
    cashSettings: "Kasseneinstellungen",
    autoCash: "Erfolgreiche Zahlungen automatisch in Kasse buchen",
    providerSelect: "POS-Anbieter wählen",
    allProviders: "Alle Anbieter",
    allCountries: "Alle Länder",
    allStatuses: "Alle Status",
    noData: "Noch keine Daten",
    startPayment: "Zahlung starten",
    testPayment: "Testzahlung",
    account: "POS-Verbindung",
    amount: "Betrag",
    paymentMethod: "Zahlungsart",
    card: "Karte / POS",
    cash: "Bar",
    bank: "Bank / Überweisung",
    payment_link: "Zahlungslink",
    qr: "QR-Zahlung",
    customerName: "Kundenname",
    description: "Beschreibung",
    sourceModule: "Quellmodul",
    transactionDate: "Transaktionsdatum",
    dateFrom: "Von",
    dateTo: "Bis",
    search: "Suchen",
    exportCsv: "CSV Export",
    reportSummary: "Berichtszusammenfassung",
    dailyBreakdown: "Tägliche Aufteilung",
    completedTotal: "Erfolgreich Gesamt",
    completedCount: "Erfolgreiche Zahlungen",
    failedCount: "Fehlgeschlagene Zahlungen",
    pendingCount: "Wartende Zahlungen",
    cancelledCount: "Stornierte Zahlungen",
    terminalId: "Terminal ID",
    merchantId: "Merchant ID",
    approvalCode: "Genehmigungscode",
    receiptNo: "Belegnummer",
    externalTransactionId: "POS-Transaktions-ID",
    cashProcessed: "In Kasse gebucht",
    source: "Quelle",
    logType: "Typ",
    action: "Aktion",
    message: "Nachricht",
    createdAt: "Erstellt",
    updatedAt: "Aktualisiert",
    saved: "Gespeichert",
    updated: "Aktualisiert",
    deleted: "Deaktiviert",
    connectionOk: "Verbindungseinstellungen sehen korrekt aus",
    paymentStarted: "Zahlung wurde gestartet",
    errorGeneric: "Ein Fehler ist aufgetreten",
    missingRequired: "Bitte Pflichtfelder ausfüllen",
    helpTitle: "Wie funktioniert dieses Modul?",
    help1Title: "1. Wie wird eine Kassen-POS-Verbindung hinzugefügt?",
    help1:
      "Terminal, Merchant, API-Key und Secret vom POS-Anbieter werden unter POS-Verbindungen gespeichert.",
    help2Title: "2. Wozu dient der Testmodus?",
    help2:
      "Er testet den Zahlungsfluss, bevor echte Geräte-API-Daten verfügbar sind. Erfolgreiche Simulationen können automatisch in die Kasse gebucht werden.",
    help3Title: "3. Wie funktioniert die Kassenbuchung?",
    help3:
      "Bei erfolgreicher Zahlung wird ein Einnahmeeintrag in cash_transactions erstellt und doppelte Buchung verhindert.",
    help4Title: "4. Wann wird das echte Gerät verbunden?",
    help4:
      "Sobald API/SDK-Dokumentation von Pavo, Ingenico, Verifone, Hugin, Beko, INPOS oder einem virtuellen POS-Anbieter vorliegt, wird der Adapter verbunden.",
  },
};

const COUNTRIES = [
  { code: "TR", name: "Türkiye" },
  { code: "CH", name: "İsviçre / Switzerland" },
  { code: "DE", name: "Almanya / Germany" },
  { code: "AT", name: "Avusturya / Austria" },
  { code: "FR", name: "Fransa / France" },
  { code: "NL", name: "Hollanda / Netherlands" },
  { code: "BE", name: "Belçika / Belgium" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "OTHER", name: "Diğer / Other" },
];

const CURRENCIES = ["TRY", "CHF", "EUR", "USD", "GBP"];

const PAYMENT_METHODS = ["card", "cash", "bank", "payment_link", "qr"];

const STATUS_COLORS = {
  active: "success",
  inactive: "default",
  passive: "default",
  completed: "success",
  pending: "warning",
  failed: "error",
  cancelled: "default",
  ok: "success",
  error: "error",
};

const cardSx = {
  borderRadius: { xs: 3, md: 4 },
  border: `1px solid ${BRAND.border}`,
  boxShadow: "0 14px 40px rgba(15, 23, 42, 0.07)",
  overflow: "hidden",
  minWidth: 0,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 3,
    backgroundColor: "#fff",
    minWidth: 0,
  },
  "& .MuiInputBase-input": {
    minWidth: 0,
  },
};

function tokenHeader() {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

function safeArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function safeObject(value, fallback = {}) {
  if (!value) return fallback;
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function todayISO() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

function formatDate(value, lang = "tr") {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);

  const locale = lang === "de" ? "de-DE" : lang === "en" ? "en-US" : "tr-TR";

  return d.toLocaleDateString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function formatDateTime(value, lang = "tr") {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const locale = lang === "de" ? "de-DE" : lang === "en" ? "en-US" : "tr-TR";

  return d.toLocaleString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function money(value, currency = "TRY", lang = "tr") {
  const num = Number(value || 0);
  const locale = lang === "de" ? "de-DE" : lang === "en" ? "en-US" : "tr-TR";

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency || "TRY",
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency || ""}`;
  }
}

function normalizeNumber(value) {
  if (value === "" || value === null || value === undefined) return 0;
  const normalized = String(value).replace(",", ".");
  const num = Number(normalized);
  return Number.isFinite(num) ? num : 0;
}

function csvEscape(value) {
  const text = value === null || value === undefined ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function getStatusLabel(status, t) {
  if (!status) return "-";
  return t(status) || status;
}

function getProviderFields(provider) {
  const required = safeArray(provider?.required_fields);
  const optional = safeArray(provider?.optional_fields);
  const fallback = ["terminal_id", "merchant_id"];

  const merged = [...required, ...optional];

  if (merged.length === 0) return fallback;

  return Array.from(new Set(merged));
}

function SectionTitle({ title, subtitle, right }) {
  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", md: "center" }}
      justifyContent="space-between"
      sx={{
        mb: 2,
        width: "100%",
        minWidth: 0,
      }}
    >
      <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 950,
            color: BRAND.text,
            lineHeight: 1.18,
            fontSize: { xs: 22, md: 24 },
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>

        {subtitle ? (
          <Typography
            variant="body2"
            sx={{
              color: BRAND.muted,
              mt: 0.5,
              lineHeight: 1.55,
              maxWidth: 950,
              overflowWrap: "anywhere",
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      {right ? (
        <Box sx={{ flexShrink: 0, minWidth: { xs: "100%", md: "auto" } }}>
          {right}
        </Box>
      ) : null}
    </Stack>
  );
}

function MetricCard({ label, value, helper, tone = "blue", compact = false }) {
  const isOrange = tone === "orange";
  const isRed = tone === "red";

  const bg = isRed
    ? `linear-gradient(135deg, ${BRAND.red}, #991B1B)`
    : isOrange
    ? `linear-gradient(135deg, ${BRAND.orange}, ${BRAND.orange2})`
    : `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.blue2})`;

  return (
    <Card
      sx={{
        ...cardSx,
        height: "100%",
        minHeight: compact ? 108 : { xs: 118, md: 140 },
        background: bg,
        color: "#fff",
      }}
    >
      <CardContent
        sx={{
          p: compact ? 1.5 : { xs: 1.7, md: 2.3 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minWidth: 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            opacity: 0.9,
            fontWeight: 900,
            mb: 1,
            lineHeight: 1.25,
            overflowWrap: "anywhere",
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            fontWeight: 950,
            lineHeight: 1.1,
            fontSize: { xs: 30, md: 30 },
            overflowWrap: "anywhere",
          }}
        >
          {value}
        </Typography>

        {helper ? (
          <Typography
            variant="caption"
            sx={{
              opacity: 0.9,
              mt: 0.8,
              fontWeight: 800,
              overflowWrap: "anywhere",
            }}
          >
            {helper}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

function EmptyBox({ text }) {
  return (
    <Card
      sx={{
        ...cardSx,
        p: { xs: 2.5, md: 3 },
        textAlign: "center",
        background: "linear-gradient(135deg, #fff, #F8FAFC)",
      }}
    >
      <Typography sx={{ fontWeight: 900, color: BRAND.muted }}>
        {text}
      </Typography>
    </Card>
  );
}

function ResponsiveButton({ children, sx = {}, ...props }) {
  return (
    <Button
      {...props}
      sx={{
        borderRadius: 999,
        fontWeight: 950,
        minHeight: 44,
        whiteSpace: "normal",
        px: 2,
        textTransform: "none",
        ...sx,
      }}
    >
      {children}
    </Button>
  );
}

export default function YazarKasaPanel() {
  const isMobile = useMediaQuery("(max-width:900px)");

  const [lang, setLang] = useState(
    localStorage.getItem("panel_lang") ||
      localStorage.getItem("app_lang") ||
      localStorage.getItem("lang") ||
      "tr"
  );

  const T = useMemo(() => DICT[lang] || DICT.tr, [lang]);
  const t = useCallback((key) => T[key] || DICT.tr[key] || key, [T]);

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);

  const [providers, setProviders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportSummary, setReportSummary] = useState([]);
  const [logs, setLogs] = useState([]);

  const [filters, setFilters] = useState({
    country_code: "",
    provider_key: "",
    status: "",
    q: "",
    date_from: todayISO(),
    date_to: todayISO(),
    currency: "",
  });

  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const [accountDialog, setAccountDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  const [accountForm, setAccountForm] = useState({
    provider_id: "",
    account_name: "",
    country_code: "TR",
    currency: "TRY",
    status: "active",
    connection_mode: "test",
    credentials: {},
    device_settings: {},
    cash_settings: {
      auto_cash: true,
    },
  });

  const [paymentDialog, setPaymentDialog] = useState(false);

  const [paymentForm, setPaymentForm] = useState({
    account_id: "",
    amount: "",
    currency: "TRY",
    payment_method: "card",
    customer_name: "",
    description: "",
    source_module: "manual",
    transaction_date: todayISO(),
    auto_cash: true,
    test_mode: true,
  });

  const showSnack = useCallback((message, severity = "success") => {
    setSnack({ open: true, severity, message });
  }, []);

  const apiFetch = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        ...tokenHeader(),
        ...(options.headers || {}),
      },
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || data.ok === false) {
      throw new Error(data.message || data.error || "API error");
    }

    return data;
  }, []);

  const loadProviders = useCallback(async () => {
    const data = await apiFetch(`/yazarkasa/providers?lang=${lang}`);
    setProviders(data.data || []);
  }, [apiFetch, lang]);

  const loadAccounts = useCallback(async () => {
    const params = new URLSearchParams();

    params.set("lang", lang);

    if (filters.country_code) params.set("country_code", filters.country_code);
    if (filters.provider_key) params.set("provider_key", filters.provider_key);

    // Account status sadece active / inactive olabilir.
    // failed, completed, pending ödeme durumudur; accounts'a gönderilmez.
    if (["active", "inactive", "passive"].includes(filters.status)) {
      params.set("status", filters.status);
    }

    const data = await apiFetch(`/yazarkasa/accounts?${params.toString()}`);
    setAccounts(data.data || []);
  }, [
    apiFetch,
    lang,
    filters.country_code,
    filters.provider_key,
    filters.status,
  ]);

  const loadTransactions = useCallback(async () => {
    const params = new URLSearchParams();

    params.set("lang", lang);
    params.set("limit", "150");

    if (filters.provider_key) params.set("provider_key", filters.provider_key);
    if (filters.status) params.set("status", filters.status);
    if (filters.q) params.set("q", filters.q);
    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to) params.set("date_to", filters.date_to);

    const data = await apiFetch(`/yazarkasa/transactions?${params.toString()}`);
    setTransactions(data.data || []);
  }, [
    apiFetch,
    lang,
    filters.provider_key,
    filters.status,
    filters.q,
    filters.date_from,
    filters.date_to,
  ]);

  const loadReports = useCallback(async () => {
    const params = new URLSearchParams();

    params.set("lang", lang);

    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to) params.set("date_to", filters.date_to);
    if (filters.provider_key) params.set("provider_key", filters.provider_key);
    if (filters.currency) params.set("currency", filters.currency);

    const data = await apiFetch(`/yazarkasa/reports/daily?${params.toString()}`);

    setReports(data.data || []);
    setReportSummary(data.summary || []);
  }, [
    apiFetch,
    lang,
    filters.date_from,
    filters.date_to,
    filters.provider_key,
    filters.currency,
  ]);

  const loadLogs = useCallback(async () => {
    const params = new URLSearchParams();

    params.set("lang", lang);
    params.set("limit", "100");

    if (filters.provider_key) params.set("provider_key", filters.provider_key);

    // Log status backend tarafında ok / error olarak tutuluyor.
    // failed seçilirse error'a çeviriyoruz.
    if (filters.status === "failed") {
      params.set("status", "error");
    } else if (["ok", "error"].includes(filters.status)) {
      params.set("status", filters.status);
    }

    const data = await apiFetch(`/yazarkasa/logs?${params.toString()}`);
    setLogs(data.data || []);
  }, [apiFetch, lang, filters.provider_key, filters.status]);

  const loadAll = useCallback(async () => {
    setLoading(true);

    try {
      await Promise.all([
        loadProviders(),
        loadAccounts(),
        loadTransactions(),
        loadReports(),
        loadLogs(),
      ]);
    } catch (err) {
      showSnack(err.message || t("errorGeneric"), "error");
    } finally {
      setLoading(false);
    }
  }, [
    loadProviders,
    loadAccounts,
    loadTransactions,
    loadReports,
    loadLogs,
    showSnack,
    t,
  ]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  function changeLang(nextLang) {
    setLang(nextLang);
    localStorage.setItem("panel_lang", nextLang);
    localStorage.setItem("app_lang", nextLang);
    localStorage.setItem("lang", nextLang);
    window.dispatchEvent(new Event("app_lang_change"));
  }

  const activeAccounts = useMemo(
    () => accounts.filter((account) => account.status === "active"),
    [accounts]
  );

  const selectedProvider = useMemo(() => {
    return providers.find(
      (provider) => String(provider.id) === String(accountForm.provider_id)
    );
  }, [providers, accountForm.provider_id]);

  const selectedProviderFields = useMemo(
    () => getProviderFields(selectedProvider),
    [selectedProvider]
  );

  const summary = useMemo(() => {
    return reportSummary.reduce(
      (acc, row) => {
        acc.completed_count += Number(row.completed_count || 0);
        acc.failed_count += Number(row.failed_count || 0);
        acc.pending_count += Number(row.pending_count || 0);
        acc.cancelled_count += Number(row.cancelled_count || 0);
        acc.completed_total += Number(row.completed_total || 0);
        acc.currency = row.currency || acc.currency || "TRY";
        return acc;
      },
      {
        completed_count: 0,
        failed_count: 0,
        pending_count: 0,
        cancelled_count: 0,
        completed_total: 0,
        currency: "TRY",
      }
    );
  }, [reportSummary]);

  function resetAccountForm() {
    setEditingAccount(null);
    setAccountForm({
      provider_id: "",
      account_name: "",
      country_code: "TR",
      currency: "TRY",
      status: "active",
      connection_mode: "test",
      credentials: {},
      device_settings: {},
      cash_settings: {
        auto_cash: true,
      },
    });
  }

  function openNewAccount() {
    resetAccountForm();
    setAccountDialog(true);
  }

  function openEditAccount(account) {
    setEditingAccount(account);

    setAccountForm({
      provider_id: account.provider_id || "",
      account_name: account.account_name || "",
      country_code: account.country_code || "TR",
      currency: account.currency || "TRY",
      status: account.status || "active",
      connection_mode: account.connection_mode || "test",
      credentials: account.credentials_masked || {},
      device_settings: safeObject(account.device_settings, {}),
      cash_settings: safeObject(account.cash_settings, { auto_cash: true }),
    });

    setAccountDialog(true);
  }

  async function saveAccount() {
    try {
      if (!accountForm.provider_id || !accountForm.account_name) {
        showSnack(t("missingRequired"), "warning");
        return;
      }

      const body = {
        provider_id: Number(accountForm.provider_id),
        account_name: accountForm.account_name,
        country_code: accountForm.country_code,
        currency: accountForm.currency,
        status: accountForm.status,
        connection_mode: accountForm.connection_mode,
        credentials: accountForm.credentials || {},
        public_settings: {},
        device_settings: accountForm.device_settings || {},
        cash_settings: accountForm.cash_settings || { auto_cash: true },
      };

      if (editingAccount) {
        await apiFetch(`/yazarkasa/accounts/${editingAccount.id}?lang=${lang}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
        showSnack(t("updated"));
      } else {
        await apiFetch(`/yazarkasa/accounts?lang=${lang}`, {
          method: "POST",
          body: JSON.stringify(body),
        });
        showSnack(t("saved"));
      }

      setAccountDialog(false);
      resetAccountForm();
      await loadAll();
    } catch (err) {
      showSnack(err.message || t("errorGeneric"), "error");
    }
  }

  async function testAccount(account) {
    try {
      const data = await apiFetch(
        `/yazarkasa/accounts/${account.id}/test?lang=${lang}`,
        {
          method: "POST",
          body: JSON.stringify({ lang }),
        }
      );

      showSnack(data.message || t("connectionOk"));
      await loadLogs();
    } catch (err) {
      showSnack(err.message || t("errorGeneric"), "error");
      await loadLogs();
    }
  }

  async function disableAccount(account) {
    try {
      await apiFetch(`/yazarkasa/accounts/${account.id}?lang=${lang}`, {
        method: "DELETE",
      });

      showSnack(t("deleted"));
      await loadAll();
    } catch (err) {
      showSnack(err.message || t("errorGeneric"), "error");
    }
  }

  function resetPaymentForm() {
    const first = activeAccounts[0];

    setPaymentForm({
      account_id: first?.id || "",
      amount: "",
      currency: first?.currency || "TRY",
      payment_method: "card",
      customer_name: "",
      description: "",
      source_module: "manual",
      transaction_date: todayISO(),
      auto_cash: true,
      test_mode: true,
    });
  }

  function openNewPayment() {
    resetPaymentForm();
    setPaymentDialog(true);
  }

  async function startPayment() {
    try {
      if (!paymentForm.account_id || normalizeNumber(paymentForm.amount) <= 0) {
        showSnack(t("missingRequired"), "warning");
        return;
      }

      const account = accounts.find(
        (item) => String(item.id) === String(paymentForm.account_id)
      );

      const body = {
        account_id: Number(paymentForm.account_id),
        amount: normalizeNumber(paymentForm.amount),
        currency: paymentForm.currency || account?.currency || "TRY",
        payment_method: paymentForm.payment_method,
        customer_name: paymentForm.customer_name,
        description: paymentForm.description,
        source_module: paymentForm.source_module || "manual",
        transaction_date: paymentForm.transaction_date || todayISO(),
        auto_cash: paymentForm.auto_cash,
        test_mode: paymentForm.test_mode,
        lang,
      };

      const data = await apiFetch(`/yazarkasa/payments/start?lang=${lang}`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      showSnack(data.message || t("paymentStarted"));
      setPaymentDialog(false);
      await loadAll();
    } catch (err) {
      showSnack(err.message || t("errorGeneric"), "error");
    }
  }

  async function cancelPayment(payment) {
    try {
      const data = await apiFetch(
        `/yazarkasa/payments/${payment.id}/cancel?lang=${lang}`,
        {
          method: "POST",
          body: JSON.stringify({ lang }),
        }
      );

      showSnack(data.message || t("cancelled"));
      await loadAll();
    } catch (err) {
      showSnack(err.message || t("errorGeneric"), "error");
    }
  }

  function exportReportsCsv() {
    const rows = [
      [
        "date",
        "provider",
        "currency",
        "completed_count",
        "completed_total",
        "pending_count",
        "failed_count",
        "cancelled_count",
      ],
      ...reports.map((row) => [
        row.transaction_date,
        row.provider_key,
        row.currency,
        row.completed_count,
        row.completed_total,
        row.pending_count,
        row.failed_count,
        row.cancelled_count,
      ]),
    ];

    const csv = rows
      .map((row) => row.map((cell) => csvEscape(cell)).join(";"))
      .join("\n");

    const blob = new Blob([`\uFEFF${csv}`], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `yazarkasa-pos-rapor-${todayISO()}.csv`;
    a.click();

    URL.revokeObjectURL(url);
  }

  const filterBar = (
    <Card
      sx={{
        ...cardSx,
        mb: 2,
        background: "rgba(255,255,255,0.92)",
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2.2 } }}>
        <Grid container spacing={1.2}>
          <Grid xs={12} sm={6} lg={2.4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t("dateFrom")}
              value={filters.date_from}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, date_from: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Grid>

          <Grid xs={12} sm={6} lg={2.4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t("dateTo")}
              value={filters.date_to}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, date_to: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Grid>

          <Grid xs={12} sm={6} lg={2.4}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("country")}
              value={filters.country_code}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  country_code: e.target.value,
                }))
              }
              sx={inputSx}
            >
              <MenuItem value="">{t("allCountries")}</MenuItem>
              {COUNTRIES.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.code} - {country.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid xs={12} sm={6} lg={2.4}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("provider")}
              value={filters.provider_key}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  provider_key: e.target.value,
                }))
              }
              sx={inputSx}
            >
              <MenuItem value="">{t("allProviders")}</MenuItem>
              {providers.map((provider) => (
                <MenuItem
                  key={provider.provider_key}
                  value={provider.provider_key}
                >
                  {provider.provider_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid xs={12} sm={6} lg={2.4}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("status")}
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
              sx={inputSx}
            >
              <MenuItem value="">{t("allStatuses")}</MenuItem>
              <MenuItem value="active">{t("active")}</MenuItem>
              <MenuItem value="inactive">{t("inactive")}</MenuItem>
              <MenuItem value="completed">{t("completed")}</MenuItem>
              <MenuItem value="pending">{t("pending")}</MenuItem>
              <MenuItem value="failed">{t("failed")}</MenuItem>
              <MenuItem value="cancelled">{t("cancelled")}</MenuItem>
              <MenuItem value="error">{t("failed")}</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const titleText = isMobile ? t("shortTitle") : t("title");

  const subtitleText = isMobile
    ? lang === "tr"
      ? "POS bağlantıları, ödeme işlemleri, kasa aktarımı ve raporları tek ekranda."
      : lang === "en"
      ? "POS connections, payments, cash posting and reports in one panel."
      : "POS-Verbindungen, Zahlungen, Kassenbuchung und Berichte in einem Panel."
    : t("subtitle");

  return (
    <Box
      sx={{
        width: { xs: "100vw", md: "100%" },
        maxWidth: { xs: "100vw", md: "100%" },
        minWidth: 0,
        minHeight: "100vh",
        overflowX: "clip",
        ml: { xs: "calc(50% - 50vw)", md: 0 },
        mr: { xs: "calc(50% - 50vw)", md: 0 },
        background:
          "radial-gradient(circle at top left, rgba(249,115,22,0.12), transparent 30%), radial-gradient(circle at top right, rgba(11,79,138,0.14), transparent 34%), linear-gradient(145deg, #F8FAFC 0%, #EEF2F7 100%)",
        boxSizing: "border-box",
        p: { xs: 2, sm: 2, md: 2.5 },
        pb: { xs: 5, md: 7 },

        "& *": {
          boxSizing: "border-box",
        },
        "& .MuiCard-root": {
          minWidth: 0,
          maxWidth: "100%",
        },
        "& .yazarkasa-shell .MuiCardContent-root": {
          paddingLeft: { xs: "24px !important", md: "24px !important" },
          paddingRight: { xs: "24px !important", md: "24px !important" },
        },
        "& .MuiTypography-root": {
          minWidth: 0,
          maxWidth: "100%",
        },
        "& .MuiButton-root": {
          textTransform: "none",
        },
        "& .MuiDialog-paper": {
          width: { xs: "calc(100vw - 20px)", sm: "calc(100% - 64px)" },
          maxWidth: "920px",
          m: { xs: 1, sm: 2 },
        },
      }}
    >
      <Box
        className="yazarkasa-shell"
        sx={{
          width: "100%",
          maxWidth: 1580,
          mx: "auto",
          minWidth: 0,
          overflowX: "clip",
        }}
      >
        <Card
          sx={{
            ...cardSx,
            mb: { xs: 1.5, md: 2 },
            background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.blue2})`,
            color: "#fff",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.4, md: 3 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 1.5, md: 2 }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              sx={{ minWidth: 0 }}
            >
              <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                <Chip
                  label="🧾 POS / YAZAR KASA"
                  sx={{
                    mb: 1.2,
                    bgcolor: "rgba(255,255,255,0.14)",
                    color: "#fff",
                    fontWeight: 950,
                    border: "1px solid rgba(255,255,255,0.22)",
                    maxWidth: "100%",
                  }}
                />

                <Typography
                  sx={{
                    fontWeight: 950,
                    lineHeight: 1.1,
                    fontSize: { xs: 28, sm: 34, md: 38 },
                    letterSpacing: "-0.03em",
                    maxWidth: "100%",
                    overflowWrap: "anywhere",
                  }}
                >
                  {titleText}
                </Typography>

                <Typography
                  sx={{
                    mt: 1,
                    opacity: 0.88,
                    maxWidth: 980,
                    lineHeight: 1.55,
                    fontSize: { xs: 15, md: 16 },
                    overflowWrap: "anywhere",
                  }}
                >
                  {subtitleText}
                </Typography>

                <Alert
                  severity="info"
                  sx={{
                    mt: 1.5,
                    display: { xs: "flex", md: "none" },
                    borderRadius: 3,
                    bgcolor: "rgba(255,255,255,0.12)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.18)",
                    "& .MuiAlert-icon": { color: "#fff" },
                    "& .MuiAlert-message": {
                      fontWeight: 800,
                      overflowWrap: "anywhere",
                    },
                  }}
                >
                  {t("mobileHint")}
                </Alert>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{
                  flexShrink: 0,
                  minWidth: { xs: "100%", md: "auto" },
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.8}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    bgcolor: "rgba(255,255,255,0.10)",
                    p: 0.6,
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.18)",
                    justifyContent: "center",
                    overflowX: "auto",
                  }}
                >
                  {LANGS.map((item) => (
                    <Button
                      key={item}
                      size="small"
                      onClick={() => changeLang(item)}
                      sx={{
                        minWidth: 46,
                        borderRadius: 999,
                        fontWeight: 950,
                        color: lang === item ? BRAND.blue : "#fff",
                        bgcolor: lang === item ? "#fff" : "transparent",
                        "&:hover": {
                          bgcolor:
                            lang === item ? "#fff" : "rgba(255,255,255,0.14)",
                        },
                      }}
                    >
                      {item.toUpperCase()}
                    </Button>
                  ))}
                </Stack>

                <ResponsiveButton
                  onClick={loadAll}
                  disabled={loading}
                  variant="contained"
                  sx={{
                    minWidth: { xs: "100%", sm: 120 },
                    bgcolor: BRAND.orange,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    t("refresh")
                  )}
                </ResponsiveButton>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={1.3} sx={{ mb: { xs: 1.5, md: 2 } }}>
          <Grid xs={12} sm={6} lg={3}>
            <MetricCard
              compact={isMobile}
              label={t("activeConnections")}
              value={activeAccounts.length}
              helper={`${accounts.length} ${t("connections")}`}
            />
          </Grid>

          <Grid xs={12} sm={6} lg={3}>
            <MetricCard
              compact={isMobile}
              label={t("todayPayments")}
              value={summary.completed_count}
              helper={formatDate(todayISO(), lang)}
              tone="orange"
            />
          </Grid>

          <Grid xs={12} sm={6} lg={3}>
            <MetricCard
              compact={isMobile}
              label={t("todayRevenue")}
              value={money(summary.completed_total, summary.currency, lang)}
              helper={t("completedTotal")}
            />
          </Grid>

          <Grid xs={12} sm={6} lg={3}>
            <MetricCard
              compact={isMobile}
              label={t("failedPayments")}
              value={summary.failed_count}
              helper={`${summary.pending_count} ${t("pendingPayments")}`}
              tone="red"
            />
          </Grid>
        </Grid>

        <Card
          sx={{
            ...cardSx,
            mb: { xs: 1.5, md: 2 },
            background: "rgba(255,255,255,0.94)",
          }}
        >
          <Tabs
            value={tab}
            onChange={(event, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              px: { xs: 0.5, md: 1.2 },
              maxWidth: "100%",
              overflowX: "auto",
              "& .MuiTabs-scroller": {
                overflowX: "auto !important",
              },
              "& .MuiTab-root": {
                minHeight: { xs: 56, md: 60 },
                minWidth: { xs: 142, sm: 160, md: 180 },
                px: { xs: 1.2, md: 2 },
                fontWeight: 950,
                textTransform: "none",
                color: BRAND.muted,
                whiteSpace: "nowrap",
                fontSize: { xs: "0.84rem", md: "0.92rem" },
              },
              "& .Mui-selected": {
                color: `${BRAND.orange} !important`,
              },
              "& .MuiTabs-indicator": {
                height: 4,
                borderRadius: 999,
                bgcolor: BRAND.orange,
              },
            }}
          >
            <Tab label={`📊 ${t("overview")}`} />
            <Tab label={`🔌 ${t("connections")}`} />
            <Tab label={`💳 ${t("payments")}`} />
            <Tab label={`📈 ${t("reports")}`} />
            <Tab label={`🛠 ${t("logsHelp")}`} />
          </Tabs>
        </Card>

        <Collapse in={tab === 0} unmountOnExit>
          <SectionTitle
            title={t("overview")}
            subtitle={
              lang === "tr"
                ? "POS sağlayıcıları, aktif bağlantılar, ödeme durumu ve kasa aktarımı tek merkezde."
                : lang === "en"
                ? "POS providers, active connections, payment status and cash posting are managed in one place."
                : "POS-Anbieter, aktive Verbindungen, Zahlungsstatus und Kassenbuchung werden zentral verwaltet."
            }
            right={
              <ResponsiveButton
                fullWidth
                variant="contained"
                onClick={openNewAccount}
                sx={{
                  bgcolor: BRAND.orange,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
                }}
              >
                + {t("addConnection")}
              </ResponsiveButton>
            }
          />

          <Grid container spacing={1.5}>
            {providers.map((provider) => {
              const connected = accounts.filter(
                (account) => account.provider_key === provider.provider_key
              ).length;

              const countries = safeArray(provider.countries);

              return (
                <Grid xs={12} sm={6} lg={4} xl={3} key={provider.id}>
                  <Card sx={{ ...cardSx, height: "100%" }}>
                    <CardContent
                      sx={{
                        pt: { xs: 2, md: 2 },
                        pb: { xs: 2, md: 2 },
                        pl: { xs: 3, md: 3 },
                        pr: { xs: 3, md: 3 },
                      }}
                    >
                      <Stack spacing={1.2} sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={1}
                          alignItems="flex-start"
                          sx={{ minWidth: 0 }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: BRAND.text,
                                lineHeight: 1.2,
                                overflowWrap: "anywhere",
                                fontSize: { xs: 18, md: 17 },
                              }}
                            >
                              {provider.provider_name}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                color: BRAND.muted,
                                fontWeight: 800,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {provider.provider_key} •{" "}
                              {provider.connection_type}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={provider.is_global ? "GLOBAL" : "LOCAL"}
                            sx={{
                              flexShrink: 0,
                              fontWeight: 950,
                              bgcolor: provider.is_global
                                ? BRAND.softBlue
                                : BRAND.softOrange,
                              color: provider.is_global
                                ? BRAND.blue
                                : BRAND.orange,
                            }}
                          />
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={0.7}
                          useFlexGap
                          flexWrap="wrap"
                          sx={{ minWidth: 0 }}
                        >
                          {countries.slice(0, 5).map((country) => (
                            <Chip
                              key={country}
                              size="small"
                              label={country}
                              variant="outlined"
                              sx={{ fontWeight: 850 }}
                            />
                          ))}

                          {countries.length > 5 ? (
                            <Chip
                              size="small"
                              label={`+${countries.length - 5}`}
                              variant="outlined"
                              sx={{ fontWeight: 850 }}
                            />
                          ) : null}

                          {countries.length === 0 ? (
                            <Chip
                              size="small"
                              label="GLOBAL"
                              variant="outlined"
                              sx={{ fontWeight: 850 }}
                            />
                          ) : null}
                        </Stack>

                        <Divider />

                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={1}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: BRAND.muted, fontWeight: 850 }}
                          >
                            {t("connections")}
                          </Typography>

                          <Typography
                            sx={{ fontWeight: 950, color: BRAND.blue }}
                          >
                            {connected}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Collapse>

        <Collapse in={tab === 1} unmountOnExit>
          {filterBar}

          <SectionTitle
            title={t("connections")}
            subtitle={
              lang === "tr"
                ? "Yazar kasa POS, ödeme cihazı veya sanal POS API bilgilerini güvenli şekilde kaydedin."
                : lang === "en"
                ? "Securely save fiscal POS, payment device or virtual POS API credentials."
                : "Kassen-POS-, Zahlungsgeräte- oder virtuelle POS-API-Daten sicher speichern."
            }
            right={
              <ResponsiveButton
                fullWidth
                variant="contained"
                onClick={openNewAccount}
                sx={{
                  bgcolor: BRAND.orange,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
                }}
              >
                + {t("addConnection")}
              </ResponsiveButton>
            }
          />

          {accounts.length === 0 ? (
            <EmptyBox text={t("noData")} />
          ) : (
            <Grid container spacing={1.5}>
              {accounts.map((account) => (
                <Grid xs={12} md={6} xl={4} key={account.id}>
                  <Card sx={{ ...cardSx, height: "100%" }}>
                    <CardContent
                      sx={{
                        pt: { xs: 2, md: 2 },
                        pb: { xs: 2, md: 2 },
                        pl: { xs: 3, md: 3 },
                        pr: { xs: 3, md: 3 },
                      }}
                    >
                      <Stack spacing={1.2} sx={{ minWidth: 0 }}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                          alignItems={{ xs: "stretch", sm: "flex-start" }}
                          sx={{ minWidth: 0 }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: BRAND.text,
                                lineHeight: 1.2,
                                overflowWrap: "anywhere",
                                fontSize: { xs: 18, md: 18 },
                              }}
                            >
                              {account.account_name}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                color: BRAND.muted,
                                fontWeight: 850,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {account.provider_name}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={getStatusLabel(account.status, t)}
                            color={STATUS_COLORS[account.status] || "default"}
                            sx={{
                              fontWeight: 950,
                              alignSelf: { xs: "flex-start", sm: "center" },
                            }}
                          />
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={0.8}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <Chip size="small" label={account.country_code} />
                          <Chip size="small" label={account.currency} />
                          <Chip
                            size="small"
                            label={
                              account.connection_mode === "live"
                                ? t("liveMode")
                                : t("testMode")
                            }
                            sx={{
                              fontWeight: 900,
                              bgcolor:
                                account.connection_mode === "live"
                                  ? BRAND.softBlue
                                  : BRAND.softOrange,
                              color:
                                account.connection_mode === "live"
                                  ? BRAND.blue
                                  : BRAND.orange,
                            }}
                          />
                        </Stack>

                        <Box
                          sx={{
                            p: 1.2,
                            borderRadius: 3,
                            bgcolor: "#F8FAFC",
                            border: `1px solid ${BRAND.border}`,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: BRAND.muted, fontWeight: 950 }}
                          >
                            {t("apiSettings")}
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={0.7}
                            useFlexGap
                            flexWrap="wrap"
                            mt={0.8}
                            sx={{ minWidth: 0 }}
                          >
                            {Object.keys(account.credentials_masked || {})
                              .length === 0 ? (
                              <Chip
                                size="small"
                                label="No credentials"
                                variant="outlined"
                              />
                            ) : (
                              Object.entries(
                                account.credentials_masked || {}
                              ).map(([key, value]) => (
                                <Chip
                                  key={key}
                                  size="small"
                                  label={`${key}: ${value}`}
                                  variant="outlined"
                                  sx={{
                                    maxWidth: "100%",
                                    "& .MuiChip-label": {
                                      overflowWrap: "anywhere",
                                      whiteSpace: "normal",
                                    },
                                  }}
                                />
                              ))
                            )}
                          </Stack>
                        </Box>

                        {account.last_error ? (
                          <Alert severity="error" sx={{ borderRadius: 3 }}>
                            {account.last_error}
                          </Alert>
                        ) : null}

                        <Typography
                          variant="caption"
                          sx={{
                            color: BRAND.muted,
                            fontWeight: 800,
                            overflowWrap: "anywhere",
                          }}
                        >
                          Last payment:{" "}
                          {formatDateTime(account.last_payment_at, lang)}
                        </Typography>

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                        >
                          <ResponsiveButton
                            fullWidth
                            variant="outlined"
                            onClick={() => testAccount(account)}
                          >
                            {t("test")}
                          </ResponsiveButton>

                          <ResponsiveButton
                            fullWidth
                            variant="contained"
                            onClick={() => openEditAccount(account)}
                            sx={{
                              bgcolor: BRAND.blue,
                              boxShadow: "none",
                              "&:hover": {
                                bgcolor: BRAND.blue2,
                                boxShadow: "none",
                              },
                            }}
                          >
                            {t("edit")}
                          </ResponsiveButton>

                          <ResponsiveButton
                            fullWidth
                            variant="outlined"
                            color="error"
                            onClick={() => disableAccount(account)}
                          >
                            {t("disable")}
                          </ResponsiveButton>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Collapse>

        <Collapse in={tab === 2} unmountOnExit>
          {filterBar}

          <SectionTitle
            title={t("payments")}
            subtitle={
              lang === "tr"
                ? "POS’a gönderilen ödeme denemeleri, başarılı işlemler, bekleyen işlemler ve kasa aktarımı."
                : lang === "en"
                ? "Payment attempts sent to POS, successful payments, pending payments and cash posting."
                : "An POS gesendete Zahlungen, erfolgreiche Zahlungen, wartende Zahlungen und Kassenbuchung."
            }
            right={
              <ResponsiveButton
                fullWidth
                variant="contained"
                onClick={openNewPayment}
                sx={{
                  bgcolor: BRAND.orange,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
                }}
              >
                + {t("startPayment")}
              </ResponsiveButton>
            }
          />

          {transactions.length === 0 ? (
            <EmptyBox text={t("noData")} />
          ) : (
            <Grid container spacing={1.5}>
              {transactions.map((payment) => (
                <Grid xs={12} lg={6} key={payment.id}>
                  <Card sx={{ ...cardSx, height: "100%" }}>
                    <CardContent
                      sx={{
                        pt: { xs: 2, md: 2 },
                        pb: { xs: 2, md: 2 },
                        pl: { xs: 3, md: 3 },
                        pr: { xs: 3, md: 3 },
                      }}
                    >
                      <Stack spacing={1.2} sx={{ minWidth: 0 }}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                          alignItems={{ xs: "stretch", sm: "flex-start" }}
                          sx={{ minWidth: 0 }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: BRAND.text,
                                lineHeight: 1.2,
                                overflowWrap: "anywhere",
                                fontSize: { xs: 18, md: 18 },
                              }}
                            >
                              {payment.provider_name ||
                                payment.provider_key ||
                                "POS"}{" "}
                              #{payment.id}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                color: BRAND.muted,
                                fontWeight: 850,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {payment.customer_name || "-"} •{" "}
                              {formatDateTime(payment.created_at, lang)}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={getStatusLabel(payment.payment_status, t)}
                            color={
                              STATUS_COLORS[payment.payment_status] || "default"
                            }
                            sx={{
                              fontWeight: 950,
                              alignSelf: { xs: "flex-start", sm: "center" },
                            }}
                          />
                        </Stack>

                        <Grid container spacing={1}>
                          <Grid xs={6} sm={3}>
                            <Typography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 800 }}
                            >
                              {t("amount")}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: BRAND.green,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {money(payment.amount, payment.currency, lang)}
                            </Typography>
                          </Grid>

                          <Grid xs={6} sm={3}>
                            <Typography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 800 }}
                            >
                              {t("paymentMethod")}
                            </Typography>
                            <Typography sx={{ fontWeight: 950 }}>
                              {t(payment.payment_method) ||
                                payment.payment_method}
                            </Typography>
                          </Grid>

                          <Grid xs={6} sm={3}>
                            <Typography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 800 }}
                            >
                              {t("terminalId")}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 950,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {payment.terminal_id || "-"}
                            </Typography>
                          </Grid>

                          <Grid xs={6} sm={3}>
                            <Typography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 800 }}
                            >
                              {t("cashProcessed")}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: payment.cash_processed
                                  ? BRAND.green
                                  : BRAND.amber,
                              }}
                            >
                              {payment.cash_processed ? "OK" : "-"}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Divider />

                        <Stack
                          direction="row"
                          spacing={0.8}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          {payment.external_transaction_id ? (
                            <Chip
                              size="small"
                              label={`${t("externalTransactionId")}: ${
                                payment.external_transaction_id
                              }`}
                              variant="outlined"
                            />
                          ) : null}

                          {payment.approval_code ? (
                            <Chip
                              size="small"
                              label={`${t("approvalCode")}: ${
                                payment.approval_code
                              }`}
                              variant="outlined"
                            />
                          ) : null}

                          {payment.receipt_no ? (
                            <Chip
                              size="small"
                              label={`${t("receiptNo")}: ${
                                payment.receipt_no
                              }`}
                              variant="outlined"
                            />
                          ) : null}

                          {payment.source_module ? (
                            <Chip
                              size="small"
                              label={`${t("source")}: ${
                                payment.source_module
                              }`}
                              variant="outlined"
                            />
                          ) : null}
                        </Stack>

                        {payment.description ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: BRAND.muted,
                              fontWeight: 800,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {payment.description}
                          </Typography>
                        ) : null}

                        {payment.payment_status === "pending" ||
                        payment.payment_status === "failed" ? (
                          <ResponsiveButton
                            fullWidth
                            variant="outlined"
                            color="error"
                            onClick={() => cancelPayment(payment)}
                          >
                            {t("cancelled")}
                          </ResponsiveButton>
                        ) : null}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Collapse>

        <Collapse in={tab === 3} unmountOnExit>
          {filterBar}

          <SectionTitle
            title={t("reports")}
            subtitle={
              lang === "tr"
                ? "Günlük POS ödeme raporu, başarılı toplam, bekleyen, hatalı ve iptal işlemleri gösterir."
                : lang === "en"
                ? "Daily POS payment report shows successful totals, pending, failed and cancelled payments."
                : "Täglicher POS-Zahlungsbericht mit erfolgreichen, wartenden, fehlgeschlagenen und stornierten Zahlungen."
            }
            right={
              <ResponsiveButton
                fullWidth
                variant="outlined"
                onClick={exportReportsCsv}
              >
                {t("exportCsv")}
              </ResponsiveButton>
            }
          />

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {reportSummary.map((row) => (
              <Grid xs={12} md={6} lg={4} key={row.currency}>
                <Card sx={{ ...cardSx }}>
                  <CardContent sx={{ p: { xs: 2, md: 2.3 } }}>
                    <Stack spacing={1}>
                      <Typography
                        sx={{
                          fontWeight: 950,
                          color: BRAND.text,
                          fontSize: 18,
                        }}
                      >
                        {t("reportSummary")} - {row.currency}
                      </Typography>

                      <Grid container spacing={1}>
                        <Grid xs={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: BRAND.muted, fontWeight: 800 }}
                          >
                            {t("completedTotal")}
                          </Typography>
                          <Typography
                            sx={{ fontWeight: 950, color: BRAND.green }}
                          >
                            {money(row.completed_total, row.currency, lang)}
                          </Typography>
                        </Grid>

                        <Grid xs={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: BRAND.muted, fontWeight: 800 }}
                          >
                            {t("completedCount")}
                          </Typography>
                          <Typography sx={{ fontWeight: 950 }}>
                            {row.completed_count || 0}
                          </Typography>
                        </Grid>

                        <Grid xs={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: BRAND.muted, fontWeight: 800 }}
                          >
                            {t("pendingCount")}
                          </Typography>
                          <Typography
                            sx={{ fontWeight: 950, color: BRAND.amber }}
                          >
                            {row.pending_count || 0}
                          </Typography>
                        </Grid>

                        <Grid xs={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: BRAND.muted, fontWeight: 800 }}
                          >
                            {t("failedCount")}
                          </Typography>
                          <Typography sx={{ fontWeight: 950, color: BRAND.red }}>
                            {row.failed_count || 0}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {reports.length === 0 ? (
            <EmptyBox text={t("noData")} />
          ) : (
            <Grid container spacing={1.5}>
              {reports.map((row, index) => (
                <Grid
                  xs={12}
                  md={6}
                  lg={4}
                  key={`${row.transaction_date}-${row.provider_key}-${index}`}
                >
                  <Card sx={{ ...cardSx, height: "100%" }}>
                    <CardContent sx={{ p: { xs: 2, md: 2.3 } }}>
                      <Stack spacing={1}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Box>
                            <Typography
                              sx={{ fontWeight: 950, color: BRAND.text }}
                            >
                              {formatDate(row.transaction_date, lang)}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{ color: BRAND.muted, fontWeight: 850 }}
                            >
                              {row.provider_key}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={row.currency}
                            sx={{
                              fontWeight: 950,
                              bgcolor: BRAND.softBlue,
                              color: BRAND.blue,
                            }}
                          />
                        </Stack>

                        <Divider />

                        <Grid container spacing={1}>
                          <Grid xs={6}>
                            <Typography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 800 }}
                            >
                              {t("completedTotal")}
                            </Typography>
                            <Typography
                              sx={{ fontWeight: 950, color: BRAND.green }}
                            >
                              {money(row.completed_total, row.currency, lang)}
                            </Typography>
                          </Grid>

                          <Grid xs={6}>
                            <Typography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 800 }}
                            >
                              {t("completedCount")}
                            </Typography>
                            <Typography sx={{ fontWeight: 950 }}>
                              {row.completed_count || 0}
                            </Typography>
                          </Grid>

                          <Grid xs={6}>
                            <Typography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 800 }}
                            >
                              {t("pendingCount")}
                            </Typography>
                            <Typography
                              sx={{ fontWeight: 950, color: BRAND.amber }}
                            >
                              {row.pending_count || 0}
                            </Typography>
                          </Grid>

                          <Grid xs={6}>
                            <Typography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 800 }}
                            >
                              {t("failedCount")}
                            </Typography>
                            <Typography
                              sx={{ fontWeight: 950, color: BRAND.red }}
                            >
                              {row.failed_count || 0}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Collapse>

        <Collapse in={tab === 4} unmountOnExit>
          {filterBar}

          <SectionTitle
            title={t("logsHelp")}
            subtitle={
              lang === "tr"
                ? "Bağlantı testleri, ödeme denemeleri, kasa aktarımı ve hata kayıtları burada görünür."
                : lang === "en"
                ? "Connection tests, payment attempts, cash posting and error logs appear here."
                : "Verbindungstests, Zahlungsversuche, Kassenbuchungen und Fehlerprotokolle erscheinen hier."
            }
          />

          <Grid container spacing={1.5}>
            <Grid xs={12} lg={7}>
              {logs.length === 0 ? (
                <EmptyBox text={t("noData")} />
              ) : (
                <Stack spacing={1.2}>
                  {logs.map((log) => (
                    <Card key={log.id} sx={{ ...cardSx }}>
                      <CardContent sx={{ p: { xs: 2, md: 2.3 } }}>
                        <Stack spacing={1}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            spacing={1}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontWeight: 950,
                                  color: BRAND.text,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {log.action || "-"}
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: BRAND.muted,
                                  fontWeight: 850,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {log.message || "-"}
                              </Typography>
                            </Box>

                            <Chip
                              size="small"
                              label={log.status}
                              color={STATUS_COLORS[log.status] || "default"}
                              sx={{
                                fontWeight: 950,
                                alignSelf: { xs: "flex-start", sm: "center" },
                              }}
                            />
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={0.8}
                            useFlexGap
                            flexWrap="wrap"
                          >
                            <Chip
                              size="small"
                              label={log.provider_key || "POS"}
                              variant="outlined"
                            />
                            <Chip
                              size="small"
                              label={formatDateTime(log.created_at, lang)}
                              variant="outlined"
                            />
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Grid>

            <Grid xs={12} lg={5}>
              <Card sx={{ ...cardSx }}>
                <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
                  <Typography
                    sx={{
                      fontWeight: 950,
                      fontSize: 20,
                      color: BRAND.text,
                      mb: 1.5,
                    }}
                  >
                    {t("helpTitle")}
                  </Typography>

                  {[
                    ["help1Title", "help1"],
                    ["help2Title", "help2"],
                    ["help3Title", "help3"],
                    ["help4Title", "help4"],
                  ].map(([titleKey, textKey]) => (
                    <Accordion
                      key={titleKey}
                      disableGutters
                      sx={{
                        border: `1px solid ${BRAND.border}`,
                        borderRadius: "18px !important",
                        mb: 1,
                        boxShadow: "none",
                        overflow: "hidden",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <Typography sx={{ fontWeight: 950 }}>+</Typography>
                        }
                        sx={{
                          bgcolor: "#F8FAFC",
                          "& .MuiAccordionSummary-content": {
                            minWidth: 0,
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 950,
                            color: BRAND.text,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {t(titleKey)}
                        </Typography>
                      </AccordionSummary>

                      <AccordionDetails>
                        <Typography
                          variant="body2"
                          sx={{
                            color: BRAND.muted,
                            lineHeight: 1.65,
                            fontWeight: 750,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {t(textKey)}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Collapse>
      </Box>

      <Dialog
        open={accountDialog}
        onClose={() => setAccountDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 950, color: BRAND.text }}>
          {editingAccount ? t("edit") : t("addConnection")}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Grid container spacing={1.3}>
              <Grid xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("provider")}
                  value={accountForm.provider_id}
                  onChange={(e) => {
                    const nextProviderId = e.target.value;
                    const provider = providers.find(
                      (item) => String(item.id) === String(nextProviderId)
                    );
                    const fields = getProviderFields(provider);
                    const nextCredentials = {};

                    fields.forEach((field) => {
                      nextCredentials[field] =
                        accountForm.credentials?.[field] || "";
                    });

                    setAccountForm((prev) => ({
                      ...prev,
                      provider_id: nextProviderId,
                      credentials: nextCredentials,
                    }));
                  }}
                  sx={inputSx}
                >
                  {providers.map((provider) => (
                    <MenuItem key={provider.id} value={provider.id}>
                      {provider.provider_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("accountName")}
                  value={accountForm.account_name}
                  onChange={(e) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      account_name: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("country")}
                  value={accountForm.country_code}
                  onChange={(e) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      country_code: e.target.value,
                    }))
                  }
                  sx={inputSx}
                >
                  {COUNTRIES.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.code} - {country.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("currency")}
                  value={accountForm.currency}
                  onChange={(e) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      currency: e.target.value,
                    }))
                  }
                  sx={inputSx}
                >
                  {CURRENCIES.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("status")}
                  value={accountForm.status}
                  onChange={(e) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                  sx={inputSx}
                >
                  <MenuItem value="active">{t("active")}</MenuItem>
                  <MenuItem value="inactive">{t("inactive")}</MenuItem>
                </TextField>
              </Grid>

              <Grid xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("connectionMode")}
                  value={accountForm.connection_mode}
                  onChange={(e) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      connection_mode: e.target.value,
                    }))
                  }
                  sx={inputSx}
                >
                  <MenuItem value="test">{t("testMode")}</MenuItem>
                  <MenuItem value="live">{t("liveMode")}</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Divider />

            <Box
              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: "#F8FAFC",
                border: `1px solid ${BRAND.border}`,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 950,
                  color: BRAND.text,
                  mb: 1.2,
                }}
              >
                {t("apiSettings")}
              </Typography>

              <Grid container spacing={1.2}>
                {selectedProviderFields.map((field) => (
                  <Grid xs={12} sm={6} md={4} key={field}>
                    <TextField
                      fullWidth
                      size="small"
                      label={field}
                      value={accountForm.credentials?.[field] || ""}
                      onChange={(e) =>
                        setAccountForm((prev) => ({
                          ...prev,
                          credentials: {
                            ...(prev.credentials || {}),
                            [field]: e.target.value,
                          },
                        }))
                      }
                      sx={inputSx}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: "#fff",
                border: `1px solid ${BRAND.border}`,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={accountForm.cash_settings?.auto_cash !== false}
                    onChange={(e) =>
                      setAccountForm((prev) => ({
                        ...prev,
                        cash_settings: {
                          ...(prev.cash_settings || {}),
                          auto_cash: e.target.checked,
                        },
                      }))
                    }
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 850 }}>
                    {t("autoCash")}
                  </Typography>
                }
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <ResponsiveButton onClick={() => setAccountDialog(false)}>
            {t("cancel")}
          </ResponsiveButton>

          <ResponsiveButton
            variant="contained"
            onClick={saveAccount}
            sx={{
              bgcolor: BRAND.orange,
              boxShadow: "none",
              "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
            }}
          >
            {t("save")}
          </ResponsiveButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={paymentDialog}
        onClose={() => setPaymentDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 950, color: BRAND.text }}>
          {t("startPayment")}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("account")}
              value={paymentForm.account_id}
              onChange={(e) => {
                const account = accounts.find(
                  (item) => String(item.id) === String(e.target.value)
                );

                setPaymentForm((prev) => ({
                  ...prev,
                  account_id: e.target.value,
                  currency: account?.currency || prev.currency || "TRY",
                }));
              }}
              sx={inputSx}
            >
              {activeAccounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.account_name} - {account.provider_name}
                </MenuItem>
              ))}
            </TextField>

            <Grid container spacing={1.2}>
              <Grid xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("amount")}
                  value={paymentForm.amount}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("currency")}
                  value={paymentForm.currency}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      currency: e.target.value,
                    }))
                  }
                  sx={inputSx}
                >
                  {CURRENCIES.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("paymentMethod")}
                  value={paymentForm.payment_method}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      payment_method: e.target.value,
                    }))
                  }
                  sx={inputSx}
                >
                  {PAYMENT_METHODS.map((method) => (
                    <MenuItem key={method} value={method}>
                      {t(method)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t("transactionDate")}
                  value={paymentForm.transaction_date}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      transaction_date: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={inputSx}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              size="small"
              label={t("customerName")}
              value={paymentForm.customer_name}
              onChange={(e) =>
                setPaymentForm((prev) => ({
                  ...prev,
                  customer_name: e.target.value,
                }))
              }
              sx={inputSx}
            />

            <TextField
              fullWidth
              multiline
              minRows={3}
              label={t("description")}
              value={paymentForm.description}
              onChange={(e) =>
                setPaymentForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              sx={inputSx}
            />

            <TextField
              fullWidth
              size="small"
              label={t("sourceModule")}
              value={paymentForm.source_module}
              onChange={(e) =>
                setPaymentForm((prev) => ({
                  ...prev,
                  source_module: e.target.value,
                }))
              }
              sx={inputSx}
            />

            <Box
              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: "#F8FAFC",
                border: `1px solid ${BRAND.border}`,
              }}
            >
              <Stack spacing={0.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!paymentForm.auto_cash}
                      onChange={(e) =>
                        setPaymentForm((prev) => ({
                          ...prev,
                          auto_cash: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 850 }}>
                      {t("autoCash")}
                    </Typography>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!paymentForm.test_mode}
                      onChange={(e) =>
                        setPaymentForm((prev) => ({
                          ...prev,
                          test_mode: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 850 }}>
                      {t("testMode")}
                    </Typography>
                  }
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <ResponsiveButton onClick={() => setPaymentDialog(false)}>
            {t("cancel")}
          </ResponsiveButton>

          <ResponsiveButton
            variant="contained"
            onClick={startPayment}
            sx={{
              bgcolor: BRAND.orange,
              boxShadow: "none",
              "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
            }}
          >
            {t("startPayment")}
          </ResponsiveButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3600}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          sx={{
            width: "100%",
            borderRadius: 3,
            fontWeight: 850,
          }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}